// crm-frontend/src/api/rpc/rpcAuth.ts
// RPC client for Auth domain - migrated from REST to RPC

import { apiClient } from '../apiClient';
import { AxiosError } from 'axios';
import { isSuperadminPath } from '../../utils/routeHelpers';

/**
 * Helper to extract company slug from current URL path
 * CRITICAL: Returns empty string if company not found to prevent malformed URLs
 * Note: This file supports public endpoints (no company), so empty is valid for those cases
 * CRITICAL: For superadmin routes, returns empty string (superadmin does not use company slug)
 */
const getCompanySlug = (): string => {
  if (typeof window === 'undefined') return '';
  
  const pathname = window.location.pathname;
  
  // CRITICAL: Superadmin routes do NOT have a company slug
  if (isSuperadminPath(pathname)) {
    return '';
  }
  
  const parts = pathname.split('/').filter(Boolean);
  const company = parts[0] || '';
  
  // CRITICAL FIX: Validate company slug, but allow empty for public endpoints
  // Only warn if company looks invalid (not just empty)
  if (company && (company === 'undefined' || company.trim() === '')) {
    console.warn('[rpcAuth] Company slug is invalid:', {
      pathname: window.location.pathname,
      parts,
    });
    return '';
  }
  
  return company;
};

/**
 * Helper to validate company slug for company-scoped endpoints
 * CRITICAL: Throws error if company is invalid
 * CRITICAL: For superadmin routes, returns empty string and does NOT throw
 */
const validateCompany = (company: string | undefined, functionName: string): string => {
  if (typeof window !== 'undefined') {
    const pathname = window.location.pathname;
    
    // CRITICAL: Superadmin routes do NOT require company validation
    if (isSuperadminPath(pathname)) {
      console.warn(`[rpcAuth] Skipping company validation for superadmin route: ${functionName}`, {
        pathname,
        providedCompany: company,
      });
      // Return empty string for superadmin (caller should handle this)
      return '';
    }
  }
  
  const companySlug = company || getCompanySlug();
  if (!companySlug || companySlug === 'undefined' || companySlug.trim() === '') {
    const error = new Error(`Company slug is required for ${functionName}. Please ensure you are on a valid company page.`);
    console.error(`[rpcAuth] ${functionName} failed:`, error.message, {
      pathname: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
      providedCompany: company,
    });
    throw error;
  }
  return companySlug;
};

/**
 * Build RPC endpoint URL
 * @param path - The endpoint path (e.g., '/login', '/logout')
 * @param company - Company slug for company-scoped endpoints, or null/undefined for public endpoints
 * @returns The full URL path
 * CRITICAL: Validates company if provided for company-scoped endpoints
 */
const buildUrl = (path: string, company: string | null | undefined): string => {
  // Explicitly check for null/undefined (not just falsy) to distinguish public vs company endpoints
  if (company === null || company === undefined) {
    // Public endpoint (no company in URL)
    // Example: /api/rpc/auth/check-company-auth
    return `/api/rpc/auth${path}`;
  }
  
  // CRITICAL FIX: Validate company for company-scoped endpoints
  if (company === 'undefined' || company.trim() === '') {
    const error = new Error('Company slug is invalid for company-scoped endpoint. Please ensure you are on a valid company page.');
    console.error('[rpcAuth] buildUrl failed:', error.message, {
      pathname: window.location.pathname,
      path,
      providedCompany: company,
    });
    throw error;
  }
  
  // Company-scoped endpoint
  // Example: /api/test/rpc/auth/login
  return `/api/${company}/rpc/auth${path}`;
};

/**
 * RPC client for auth-related operations
 * All endpoints return { ok: true, data } or { ok: false, error }
 */
const rpcAuth = {
  /**
   * Check if company exists (public endpoint)
   * POST /api/rpc/auth/check-company-auth (no company in URL)
   * @param company - Company slug to check (sent in request body, not URL)
   */
  async checkCompanyAuth(company: string) {
    // Public endpoint - explicitly pass null to use /api/rpc/auth/check-company-auth
    const { data } = await apiClient.post(buildUrl('/check-company-auth', null), { company }, {
      timeout: 1500,
      validateStatus: (status) => status >= 200 && status < 500,
    });
    if (data?.ok) {
      return data.data as { exists: boolean };
    }
    return { exists: true }; // Fail open
  },

  /**
   * Login (superadmin or regular)
   * POST /api/:company/rpc/auth/login (company-scoped)
   * @param payload - Login credentials
   * @param company - Company slug (required for company-scoped endpoint)
   */
  async login(payload: {
    email: string;
    password: string;
    companyNumber?: string;
    device_id?: string;
    device_name?: string;
    forceLogout?: boolean;
  }, company: string) {
    // Company-scoped endpoint - require company parameter
    if (!company) {
      throw new Error('Company parameter is required for login');
    }
    
    try {
      console.debug('[rpcAuth.login] Sending request:', {
        company,
        email: payload.email,
        hasDeviceId: !!payload.device_id,
      });

      // Get device-specific timeout (60s for mobile, 30s for desktop)
      const { getLoginTimeout } = await import('../../utils/deviceDetection');
      const loginTimeout = getLoginTimeout();
      
      const response = await apiClient.post(buildUrl('/login', company), payload, {
        validateStatus: (status) => status >= 200 && status < 500,
        timeout: loginTimeout, // 30s desktop, 60s mobile - login involves IP checks, device registration, session creation
      });
      
      const { data } = response;
      
      console.debug('[rpcAuth.login] Response received:', {
        ok: data?.ok,
        status: data?.status,
        step: data?.data?.step,
        hasError: !!data?.error,
      });
      
      // Handle OTHER_SESSION_ACTIVE status - this is a successful response (200 OK) with special status
      if (data?.status === 'OTHER_SESSION_ACTIVE') {
        return {
          status: 'OTHER_SESSION_ACTIVE',
          message: data.message || 'Account is already logged in from another device',
          sessions: data.sessions || []
        };
      }
      
      if (data?.ok && data.data) {
        return data.data; // Returns { step, message } or { success: true, client, token }
      }
      
      // Handle error response - create Axios-like error structure
      // Extract error message (handle both string and object formats)
      let errorMessage = 'Login failed';
      if (typeof data?.error === 'string') {
        errorMessage = data.error;
      } else if (typeof data?.error === 'object' && data.error?.message) {
        errorMessage = data.error.message;
      } else if (typeof data?.error === 'object' && data.error?.code) {
        errorMessage = data.error.code;
      }
      
      const error = new Error(errorMessage) as AxiosError;
      (error as any).response = {
        status: response.status || 400,
        statusText: response.statusText || 'Bad Request',
        data: {
          ok: false,
          error: typeof data?.error === 'string' ? data.error : data?.error,
          ...data
        }
      };
      (error as any).isAxiosError = true;
      throw error;
    } catch (err: any) {
      console.error('[rpcAuth.login] Error:', {
        message: err.message,
        status: err.response?.status || err.status,
        error: err.response?.data?.error || err.response?.data?.message,
        code: err.code,
        isTimeout: err.code === 'ECONNABORTED' || err.message?.includes('timeout'),
      });

      // Handle timeout errors specifically
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout') || err.message?.includes('Timeout')) {
        // Get device-specific error message
        const { isMobileDevice } = await import('../../utils/deviceDetection');
        const isMobile = isMobileDevice();
        
        const timeoutMessage = isMobile 
          ? 'החיבור איטי. אנא בדוק את חיבור האינטרנט שלך ונסה שוב. אם הבעיה נמשכת, נסה להתחבר מרשת WiFi.'
          : 'התהליך לוקח יותר זמן מהצפוי. אנא נסה שוב.';
        
        const timeoutError = new Error(timeoutMessage) as AxiosError;
        (timeoutError as any).response = {
          status: 408,
          statusText: 'Request Timeout',
          data: {
            ok: false,
            error: timeoutMessage
          }
        };
        (timeoutError as any).isAxiosError = true;
        (timeoutError as any).code = 'ECONNABORTED';
        throw timeoutError;
      }

      // Network errors
      if (err.code === 'ECONNREFUSED' || err.code === 'ETIMEDOUT' || err.code === 'ENOTFOUND') {
        const networkError = new Error('לא ניתן להתחבר לשרת. אנא בדוק את החיבור לאינטרנט ונסה שוב.') as AxiosError;
        (networkError as any).response = {
          status: 503,
          statusText: 'Service Unavailable',
          data: {
            ok: false,
            error: 'לא ניתן להתחבר לשרת. אנא בדוק את החיבור לאינטרנט ונסה שוב.'
          }
        };
        (networkError as any).isAxiosError = true;
        throw networkError;
      }

      // If it's already an Axios error, re-throw it
      if (err?.response || err?.isAxiosError) {
        throw err;
      }
      // Otherwise, wrap it in an Axios-like structure
      const error = new Error(err?.message || 'Login failed') as AxiosError;
      (error as any).response = {
        status: 400,
        statusText: 'Bad Request',
        data: {
          ok: false,
          error: err?.message || 'Login failed'
        }
      };
      (error as any).isAxiosError = true;
      throw error;
    }
  },

  /**
   * Force login - revoke other sessions and create new one
   * POST /api/:company/rpc/auth/force-login (company-scoped)
   * @param payload - Login credentials and device info
   * @param company - Company slug (required for company-scoped endpoint)
   */
  async forceLogin(payload: {
    email: string;
    password: string;
    companyNumber?: string;
    deviceId?: string;
    device_id?: string;
    device_name?: string;
    userAgent?: string;
    ip?: string;
  }, company: string) {
    // Company-scoped endpoint - require company parameter
    if (!company) {
      throw new Error('Company parameter is required for force-login');
    }
    
    try {
      console.debug('[rpcAuth.forceLogin] Sending request:', {
        company,
        email: payload.email,
      });

      const response = await apiClient.post(buildUrl('/force-login', company), payload, {
        validateStatus: (status) => status >= 200 && status < 500,
        timeout: 30000, // 30 seconds - force login involves session revocation and creation
      });
      
      const { data } = response;
      
      if (data?.ok) {
        // Backend returns: { ok: true, success: true, accessToken: "...", refreshToken: "...", user: {...}, client: {...}, token: "..." }
        // Return in the same format as regular login
        return {
          success: data.success || true,
          client: data.user || data.client,
          user: data.user || data.client, // Alias for compatibility
          token: data.accessToken || data.token
        };
      }
      
      // Handle error response
      let errorMessage = 'Force login failed';
      if (typeof data?.error === 'string') {
        errorMessage = data.error;
      } else if (typeof data?.error === 'object' && data.error?.message) {
        errorMessage = data.error.message;
      }
      
      const error = new Error(errorMessage) as AxiosError;
      (error as any).response = {
        status: response.status || 400,
        statusText: response.statusText || 'Bad Request',
        data: {
          ok: false,
          error: typeof data?.error === 'string' ? data.error : data?.error,
          ...data
        }
      };
      (error as any).isAxiosError = true;
      throw error;
    } catch (err: any) {
      // If it's already an Axios error, re-throw it
      if (err?.response || err?.isAxiosError) {
        throw err;
      }
      // Otherwise, wrap it in an Axios-like structure
      const error = new Error(err?.message || 'Force login failed') as AxiosError;
      (error as any).response = {
        status: 400,
        statusText: 'Bad Request',
        data: {
          ok: false,
          error: err?.message || 'Force login failed'
        }
      };
      (error as any).isAxiosError = true;
      throw error;
    }
  },

  /**
   * Verify OTP (superadmin or regular)
   */
  async verifyOtp(payload: { email: string; otp: string }, company?: string) {
    const companySlug = company || getCompanySlug();
    // CRITICAL FIX: Validate company before building URL
    if (!companySlug || companySlug === 'undefined' || companySlug.trim() === '') {
      throw new Error('Company slug is required for verifyOtp. Please ensure you are on a valid company page.');
    }
    const { data } = await apiClient.post(buildUrl('/verify-otp', companySlug), payload, {
      validateStatus: (status) => status >= 200 && status < 500,
    });
    
    if (data?.ok && data.data) {
      return data.data; // Returns { message, token, client }
    }
    
    throw new Error(data?.error || 'Invalid OTP');
  },

  /**
   * Verify 2FA
   * CRITICAL: This is a public endpoint - do NOT send Authorization header
   */
  async verify2FA(
    payload: {
      email: string;
      otp?: string;
      backupCode?: string;
      device_id?: string;
      device_name?: string;
    },
    company?: string
  ) {
    const companySlug = validateCompany(company, 'verify2FA');
    
    try {
      const { data } = await apiClient.post(buildUrl('/verify-2fa', companySlug), payload, {
        validateStatus: (status) => status >= 200 && status < 500,
        timeout: 30000, // 30 seconds - 2FA verification can take time
        // Note: Authorization header is automatically removed by apiClient interceptor for public auth endpoints
      });
      
      if (data?.ok && data.data) {
        return data.data; // Returns { success: true, client, token }
      }
      
      // Extract error message from response
      const errorMessage = data?.error || '2FA verification failed';
      const error = new Error(errorMessage) as AxiosError;
      (error as any).response = {
        status: 400,
        statusText: 'Bad Request',
        data: {
          ok: false,
          error: errorMessage,
          ...data
        }
      };
      (error as any).isAxiosError = true;
      throw error;
    } catch (err: any) {
      // Handle timeout errors
      if (err?.code === 'ECONNABORTED' || err?.message?.includes('timeout') || err?.isTimeout) {
        const timeoutError = new Error('התהליך לוקח יותר זמן מהצפוי. אנא נסה שוב.') as AxiosError;
        (timeoutError as any).response = {
          status: 408,
          statusText: 'Request Timeout',
          data: {
            ok: false,
            error: 'התהליך לוקח יותר זמן מהצפוי. אנא נסה שוב.'
          }
        };
        (timeoutError as any).isAxiosError = true;
        throw timeoutError;
      }
      
      // If it's already an Axios error, re-throw it
      if (err?.response || err?.isAxiosError) {
        throw err;
      }
      
      // Otherwise, wrap it in an Axios-like structure
      const error = new Error(err?.message || '2FA verification failed') as AxiosError;
      (error as any).response = {
        status: err?.response?.status || 500,
        statusText: err?.response?.statusText || 'Internal Server Error',
        data: {
          ok: false,
          error: err?.response?.data?.error || err?.message || '2FA verification failed'
        }
      };
      (error as any).isAxiosError = true;
      throw error;
    }
  },

  /**
   * Resend 2FA OTP
   */
  async resend2FA(payload: { email: string }, company?: string) {
    const companySlug = validateCompany(company, 'resend2FA');
    const { data } = await apiClient.post(buildUrl('/resend-2fa', companySlug), payload, {
      validateStatus: (status) => status >= 200 && status < 500,
    });
    
    if (data?.ok && data.data) {
      return data.data;
    }
    
    throw new Error(data?.error || 'Failed to resend 2FA OTP');
  },

  /**
   * Send reset password OTP
   */
  async sendResetOtp(payload: { email: string }, company?: string) {
    const companySlug = validateCompany(company, 'sendResetOtp');
    const { data } = await apiClient.post(buildUrl('/send-reset-otp', companySlug), payload, {
      validateStatus: (status) => status >= 200 && status < 500,
      timeout: 60000, // 60 seconds - email sending can take 20+ seconds, need extra buffer
    });
    
    if (!data?.ok) {
      throw new Error(data?.error || 'Failed to send reset OTP');
    }
  },

  /**
   * Reset password
   */
  async resetPassword(
    payload: {
      email: string;
      otp: string;
      newPassword: string;
    },
    company?: string
  ) {
    const companySlug = validateCompany(company, 'resetPassword');
    const { data } = await apiClient.post(buildUrl('/reset-password', companySlug), payload, {
      validateStatus: (status) => status >= 200 && status < 500,
    });
    
    if (!data?.ok) {
      throw new Error(data?.error || 'Failed to reset password');
    }
  },

  /**
   * Logout
   * POST /api/:company/rpc/auth/logout (company-scoped)
   * @param company - Company slug (required for company-scoped endpoint)
   */
  async logout(company: string) {
    // Company-scoped endpoint - require company parameter
    if (!company) {
      throw new Error('Company parameter is required for logout');
    }
    const { data } = await apiClient.post(buildUrl('/logout', company), {}, {
      validateStatus: (status) => status >= 200 && status < 500,
    });
    
    if (!data?.ok) {
      throw new Error(data?.error || 'Logout failed');
    }
  },

  /**
   * Get current authenticated user (auth/me)
   */
  async getAuthMe(company?: string) {
    const companySlug = validateCompany(company, 'getAuthMe');
    const { data } = await apiClient.post(buildUrl('/get-auth-me', companySlug), {}, {
      validateStatus: (status) => status >= 200 && status < 500,
    });
    
    if (data?.ok && data.data) {
      return data.data;
    }
    
    throw new Error(data?.error || 'Failed to get user info');
  },

  /**
   * Get client profile with bookings (client/me)
   */
  async getClientMe(company?: string) {
    const companySlug = validateCompany(company, 'getClientMe');
    const { data } = await apiClient.post(buildUrl('/get-client-me', companySlug), {}, {
      validateStatus: (status) => status >= 200 && status < 500,
    });
    
    if (data?.ok && data.data) {
      return data.data; // Returns { client, bookings }
    }
    
    throw new Error(data?.error || 'Failed to get client info');
  },

  /**
   * Get announcements
   * CRITICAL: Announcements are company-scoped, NOT available for superadmin
   */
  async getAnnouncements(company?: string) {
    // CRITICAL: Check if this is a superadmin route before validating company
    if (typeof window !== 'undefined' && isSuperadminPath(window.location.pathname)) {
      console.warn('[rpcAuth] getAnnouncements called on superadmin route, returning empty list.');
      // Return empty announcements for superadmin (announcements are company-scoped)
      return { announcements: [], dismissed: [] };
    }
    
    const companySlug = validateCompany(company, 'getAnnouncements');
    
    // Additional safety check: if companySlug is empty after validation, return empty list
    if (!companySlug || companySlug === 'undefined' || companySlug.trim() === '') {
      console.warn('[rpcAuth] getAnnouncements called without valid companySlug, returning empty list.');
      return { announcements: [], dismissed: [] };
    }
    
    const { data } = await apiClient.post(buildUrl('/get-announcements', companySlug), { company: companySlug }, {
      validateStatus: (status) => status >= 200 && status < 500,
    });
    
    if (data?.ok && data.data) {
      return data.data; // Returns { announcements, dismissed }
    }
    
    throw new Error(data?.error || 'Failed to get announcements');
  },

  /**
   * Dismiss announcement
   */
  async dismissAnnouncement(id: string, company?: string) {
    const companySlug = validateCompany(company, 'dismissAnnouncement');
    const { data } = await apiClient.post(buildUrl('/dismiss-announcement', companySlug), { id, company: companySlug }, {
      validateStatus: (status) => status >= 200 && status < 500,
    });
    
    if (!data?.ok) {
      throw new Error(data?.error || 'Failed to dismiss announcement');
    }
  },

  /**
   * Get admin company name
   */
  async getAdminCompanyName(company?: string) {
    const companySlug = validateCompany(company, 'getAdminCompanyName');
    const { data } = await apiClient.post(buildUrl('/get-admin-company-name', companySlug), {}, {
      validateStatus: (status) => status >= 200 && status < 500,
    });
    
    if (data?.ok && data.data) {
      return data.data; // Returns { companyName }
    }
    
    throw new Error(data?.error || 'Failed to get company name');
  },

  /**
   * Register new client
   */
  async register(
    payload: {
      fullName: string;
      email: string;
      phone?: string;
      password: string;
      subscriptionType?: string;
      totalSessions?: number;
      startDate?: Date;
      endDate?: Date;
      adminId: string;
      role?: string;
      managerIds?: string[]; // Array of manager IDs to assign client to managers
      business?: {
        businessId?: string;
        contactPerson?: { name?: string };
        address?: string;
        businessField?: string;
        paymentCompany?: string;
        paymentProvider?: string;
      };
      contacts?: any[];
    },
    company?: string
  ) {
    const companySlug = validateCompany(company, 'register');
    const { data } = await apiClient.post(buildUrl('/register', companySlug), payload, {
      validateStatus: (status) => status >= 200 && status < 500,
    });
    
    if (data?.ok && data.data) {
      return data.data; // Returns { message, client }
    }
    
    throw new Error(data?.error || 'Registration failed');
  },

  /**
   * Send 2FA setup OTP
   */
  async send2FASetupOtp(company?: string) {
    const companySlug = validateCompany(company, 'send2FASetupOtp');
    const { data } = await apiClient.post(buildUrl('/send-2fa-setup-otp', companySlug), {}, {
      validateStatus: (status) => status >= 200 && status < 500,
    });
    
    if (!data?.ok) {
      throw new Error(data?.error || 'Failed to send 2FA setup OTP');
    }
    
    return data.data;
  },

  /**
   * Toggle 2FA
   */
  async toggle2FA(
    payload: {
      enabled: boolean;
      otp?: string;
    },
    company?: string
  ) {
    const companySlug = validateCompany(company, 'toggle2FA');
    const { data } = await apiClient.post(buildUrl('/toggle-2fa', companySlug), payload, {
      validateStatus: (status) => status >= 200 && status < 500,
    });
    
    if (data?.ok && data.data) {
      return data.data; // Returns { message, twoFactorEnabled, backupCodes? }
    }
    
    throw new Error(data?.error || 'Failed to toggle 2FA');
  },

  /**
   * Toggle login alert
   */
  async toggleLoginAlert(
    payload: {
      enabled: boolean;
    },
    company?: string
  ) {
    const companySlug = validateCompany(company, 'toggleLoginAlert');
    const { data } = await apiClient.post(buildUrl('/toggle-login-alert', companySlug), payload, {
      validateStatus: (status) => status >= 200 && status < 500,
    });
    
    if (data?.ok && data.data) {
      return data.data; // Returns { message, loginAlertEnabled }
    }
    
    throw new Error(data?.error || 'Failed to toggle login alert');
  },

  /**
   * Change password
   */
  async changePassword(
    payload: {
      currentPassword: string;
      newPassword: string;
    },
    company?: string
  ) {
    const companySlug = validateCompany(company, 'changePassword');
    const { data } = await apiClient.post(buildUrl('/change-password', companySlug), payload, {
      validateStatus: (status) => status >= 200 && status < 500,
    });
    
    if (!data?.ok) {
      throw new Error(data?.error || 'Failed to change password');
    }
    
    return data.data;
  },

  /**
   * Send email change OTP
   */
  async sendEmailChangeOtp(
    payload: {
      newEmail: string;
    },
    company?: string
  ) {
    const companySlug = validateCompany(company, 'sendEmailChangeOtp');
    const { data } = await apiClient.post(buildUrl('/send-email-change-otp', companySlug), payload, {
      validateStatus: (status) => status >= 200 && status < 500,
    });
    
    if (!data?.ok) {
      throw new Error(data?.error || 'Failed to send email change OTP');
    }
    
    return data.data;
  },

  /**
   * Change email
   */
  async changeEmail(
    payload: {
      newEmail: string;
      otp: string;
    },
    company?: string
  ) {
    const companySlug = validateCompany(company, 'changeEmail');
    const { data } = await apiClient.post(buildUrl('/change-email', companySlug), payload, {
      validateStatus: (status) => status >= 200 && status < 500,
    });
    
    if (!data?.ok) {
      throw new Error(data?.error || 'Failed to change email');
    }
    
    return data.data;
  },

  /**
   * Update phone
   */
  async updatePhone(
    payload: {
      phone: string;
    },
    company?: string
  ) {
    const companySlug = validateCompany(company, 'updatePhone');
    const { data } = await apiClient.post(buildUrl('/update-phone', companySlug), payload, {
      validateStatus: (status) => status >= 200 && status < 500,
    });
    
    if (data?.ok && data.data) {
      return data.data; // Returns { message, phone }
    }
    
    throw new Error(data?.error || 'Failed to update phone');
  },

  /**
   * Update admin client password
   */
  async updateAdminClientPassword(
    payload: {
      clientId: string;
      newPassword: string;
    },
    company?: string
  ) {
    const companySlug = validateCompany(company, 'updateAdminClientPassword');
    const { data } = await apiClient.post(buildUrl('/update-admin-client-password', companySlug), payload, {
      validateStatus: (status) => status >= 200 && status < 500,
    });
    
    if (!data?.ok) {
      throw new Error(data?.error || 'Failed to update client password');
    }
    
    return data.data;
  },
};

export default rpcAuth;

